import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { PacientesRecurrencia } from '@/application/metrics';
import { getPacientesRecurrencia } from '@/infrastructure/http/metricsApi';
import { useAuth } from '@/presentation/contexts/useAuth';
import { MetricBlock } from './MetricBlock';
import { MetricKpiCard } from './MetricKpiCard';
import { formatInteger } from './metricFormatters';

interface RecurrenceBlockProps {
  tenantId: string;
  year: number;
  month: number;
}

/** Recurrencia de pacientes de la clínica seleccionada (nuevos vs recurrentes). Se auto-carga. */
export function RecurrenceBlock({ tenantId, year, month }: RecurrenceBlockProps) {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<PacientesRecurrencia | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId || !year || !month) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    getPacientesRecurrencia(token, { tenantId, year, month })
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, tenantId, year, month, t]);

  const openDetail = (tipo: 'todos' | 'nuevo' | 'recurrente') => () => {
    const params = new URLSearchParams({
      tenant: tenantId,
      year: String(year),
      month: String(month),
      tipo,
    });
    navigate(`/reports/conversion?${params.toString()}`);
  };

  return (
    <MetricBlock
      title={t('metrics.recurrence.title')}
      description={t('metrics.recurrence.description')}
      isLoading={isLoading}
      error={error}
      isEmpty={!data}
      emptyMessage={t('metrics.states.empty.message')}
    >
      {data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MetricKpiCard label={t('metrics.recurrence.attended')} value={formatInteger(data.atendidos, i18n.language)} tone="indigo" onDetail={openDetail('todos')} />
          <MetricKpiCard label={t('metrics.recurrence.new')} value={formatInteger(data.nuevos, i18n.language)} tone="blue" onDetail={openDetail('nuevo')} />
          <MetricKpiCard label={t('metrics.recurrence.returning')} value={formatInteger(data.recurrentes, i18n.language)} tone="green" onDetail={openDetail('recurrente')} />
          <MetricKpiCard label={t('metrics.recurrence.rate')} value={`${data.tasaRecurrencia}%`} tone="amber" />
        </div>
      )}
    </MetricBlock>
  );
}
